import { CallConnectionState } from 'Components/CallConnectionStatus/index.types';
import { EventEmitter } from 'events';
import moment from 'moment';
import { getCurrentBrowser } from 'Utils/getBrowser';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { VoipAlertAdapter } from './adapters/voipAlertAdapter';
import { VoipQualityAdapter } from './adapters/voipQualityAdapter';
import {
  isInboundAudioReport,
  isOutboundAudioReport,
  isCandidatePairReport,
  isCodecReport,
  isRemoteInboundRtpReport,
} from './guards';
import {
  VOIP_CALL_QUALITY_TRACKS_INTERVAL,
  MAX_METRICS_PER_CALL,
  VOIP_CALL_QUALITY_SNAPSHOT_INTERVAL,
} from './index.constants';
import {
  RTCStatsReportType,
  WebRTCStatsOptions,
  CollectedStats,
  CallStats,
  VoipMonitorEvent,
  VoipMonitorParams,
  VoipCallQualitySnapshot,
} from './index.types';
import {
  calculateMetricAverage,
  calculateMosScore,
  calculatePacketLossRate,
} from './utils';

export class VoipMonitorService extends EventEmitter {
  private static instance: VoipMonitorService;
  private peerConnection: RTCPeerConnection | null = null;
  private tracksIntervalId: ReturnType<typeof setInterval> | null = null;
  private snapshotIntervalId: ReturnType<typeof setInterval> | null = null;
  private metricsHistory: CollectedStats[] = [];
  private lastProcessedIndex = 0;
  private callId = '';
  private personId = '';
  private options: WebRTCStatsOptions;
  private isOnline: boolean = navigator.onLine;
  private qualityAdapter?: VoipQualityAdapter;
  private lastConnectionState: CallConnectionState = 'stable';

  private constructor(options: WebRTCStatsOptions = {}) {
    super();
    this.options = {
      tracksInterval: VOIP_CALL_QUALITY_TRACKS_INTERVAL,
      snapshotInterval: VOIP_CALL_QUALITY_SNAPSHOT_INTERVAL,
      maxMetricsPerCall: MAX_METRICS_PER_CALL,
      sendFullCallStats: false,
      enableAlertAdapter: false,
      ...options,
    };

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /**
   * Get the instance of the VoipMonitorService
   * @param options - The options for the VoipMonitorService
   * @returns The instance of the VoipMonitorService
   */
  public static getInstance(options?: WebRTCStatsOptions): VoipMonitorService {
    if (!VoipMonitorService.instance) {
      VoipMonitorService.instance = new VoipMonitorService(options);
    } else if (options) {
      VoipMonitorService.instance.updateOptions(options);
    }
    return VoipMonitorService.instance;
  }

  /**
   * Update the options for the VoipMonitorService
   * @param options - The options for the VoipMonitorService
   */
  public updateOptions(options: WebRTCStatsOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Start monitoring the voip connection
   * @param params - The parameters for the VoipMonitorService
   */
  public startMonitoring({
    personId,
    callId,
    session,
  }: VoipMonitorParams): void {
    this.stopMonitoring();

    if (!session) {
      this.emit(VoipMonitorEvent.ERROR, new Error('No session provided'));
      return;
    }

    this.peerConnection =
      session?.sessionDescriptionHandler?.peerConnection || null;
    this.personId = personId;
    this.callId = callId;
    this.metricsHistory = [];
    this.lastConnectionState = 'stable';

    if (!this.peerConnection) {
      this.emit(
        VoipMonitorEvent.ERROR,
        new Error('No peer connection available')
      );
      return;
    }

    this.qualityAdapter = new VoipQualityAdapter(
      this.callId,
      this.personId,
      this.isOnline,
      this.peerConnection
    );

    this.peerConnection.addEventListener(
      'connectionstatechange',
      this.handleConnectionStateChange
    );
    this.peerConnection.addEventListener(
      'iceconnectionstatechange',
      this.handleIceConnectionStateChange
    );
    this.startCollection();
  }

  /**
   * Handle the connection state change
   */
  private handleConnectionStateChange = (): void => {
    if (!this.peerConnection) return;
    this.checkConnectionStatus();
  };

  /**
   * Handle the ice connection state change
   */
  private handleIceConnectionStateChange = (): void => {
    if (!this.peerConnection) return;
    this.checkConnectionStatus();
  };

  /**
   * Check the connection status
   */
  private checkConnectionStatus(): void {
    if (!this.qualityAdapter) return;

    const newState = this.qualityAdapter.determineConnectionState();
    if (newState !== this.lastConnectionState) {
      this.lastConnectionState = newState;
      this.emit(VoipMonitorEvent.CONNECTION_QUALITY_CHANGED, newState);
    }
  }

  /**
   * Start the collection of stats
   */
  private startCollection(): void {
    if (!this.peerConnection) {
      this.emit(
        VoipMonitorEvent.ERROR,
        new Error('No peer connection available')
      );
      return;
    }

    this.emit(VoipMonitorEvent.MONITORING_STARTED, this.callId);

    this.tracksIntervalId = setInterval(
      () => void this.collectStats(),
      this.options.tracksInterval
    );

    this.snapshotIntervalId = setInterval(
      () => void this.collectSnapshots(),
      this.options.snapshotInterval
    );
  }

  /**
   * Stop monitoring the voip connection
   */
  public stopMonitoring(): void {
    if (this.tracksIntervalId || this.snapshotIntervalId) {
      const callStats = this.generateCallStats();
      this.emit(VoipMonitorEvent.MONITORING_ENDED, this.callId, callStats);
      this.cleanUp();
    }
  }

  /**
   * Collects and processes WebRTC statistics from the peer connection.
   * This method:
   * - Retrieves raw stats from the RTCPeerConnection
   * - Processes the stats through the quality adapter
   * - Updates metrics history
   * - Emits connection quality change events when necessary
   * - Handles browser-specific timing adjustments
   * - Optionally processes alerts through VoipAlertAdapter
   *
   * @returns A promise that resolves when stats collection is complete
   */
  private async collectStats(): Promise<void> {
    if (!this.peerConnection || !this.qualityAdapter) return;

    try {
      // Firefox-specific fallback
      const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

      if (isFirefox) {
        // Dar mais tempo para o Firefox processar
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const statsReport = await this.peerConnection.getStats();

      // Firefox pode retornar um Map vazio ou undefined
      if (!statsReport || statsReport.size === 0) {
        console.warn(
          'VoipMonitorService: Empty stats report received (Firefox)'
        );
        return;
      }

      const collectedStats = this.processStatsReport(statsReport);

      this.metricsHistory.push(collectedStats);

      if (this.metricsHistory.length > this.options.maxMetricsPerCall) {
        this.metricsHistory.shift();
      }

      const connectionState = this.qualityAdapter.analyzeQuality(
        collectedStats,
        this.metricsHistory
      );

      if (connectionState !== this.lastConnectionState) {
        // Avoid emitting the same connection state multiple times
        this.lastConnectionState = connectionState;
        this.emit(VoipMonitorEvent.CONNECTION_QUALITY_CHANGED, connectionState);
      }

      if (this.options.enableAlertAdapter) {
        await VoipAlertAdapter.checkStats(
          this.personId,
          this.callId,
          collectedStats
        );
      }
    } catch (error) {
      console.error('VoipMonitorService: Error collecting stats', error);
      this.emit(VoipMonitorEvent.ERROR, error as Error);
    }
  }

  /**
   * Process the stats report
   * @param statsReport - The stats report
   * @returns The collected stats
   */
  private processStatsReport(statsReport: RTCStatsReport): CollectedStats {
    const collectedStats: CollectedStats = {
      time: moment.utc().format(),
      raw: [],
      basicStats: {},
    };

    const browser = getCurrentBrowser();

    statsReport.forEach((report: RTCStatsReportType) => {
      collectedStats.raw.push(report);

      if (isInboundAudioReport(report)) {
        collectedStats.inboundAudio = report;

        collectedStats.basicStats.jitter = report.jitter;
        collectedStats.basicStats.packetsReceived = report.packetsReceived;
        collectedStats.basicStats.packetsLost = report.packetsLost;
        return;
      }

      if (isOutboundAudioReport(report)) {
        collectedStats.outboundAudio = report;
        return;
      }

      if (isCandidatePairReport(report)) {
        collectedStats.candidatePair = report;

        if (browser !== 'firefox') {
          collectedStats.basicStats.availableBitrate =
            report.availableOutgoingBitrate;
          collectedStats.basicStats.roundTripTime = report.currentRoundTripTime;
        }
        return;
      }

      if (isRemoteInboundRtpReport(report)) {
        if (browser === 'firefox') {
          collectedStats.basicStats.roundTripTime = report.roundTripTime;
        }
        return;
      }
    });

    return collectedStats;
  }

  /**
   * Calculates quality metrics for the given direction (inbound or outbound)
   *
   * For inbound metrics, we include packetsLost and fractionLost because:
   * 1. The receiver (inbound) can directly detect which packets didn't arrive
   * 2. This is the most accurate and reliable measurement of packet loss
   *
   * Note: Although it's possible to calculate an approximation of packet loss for outbound
   * using candidatePair (through packetsSent and responsesReceived), we chose not to because:
   * 1. It would be less accurate than direct inbound measurement
   * 2. RTCP responses may not correspond 1:1 with sent packets
   * 3. Some packets might be retransmitted, affecting calculation accuracy
   * 4. Not all browsers expose these candidatePair statistics in the same way
   *
   * @param metrics - Array of collected stats to calculate metrics from
   * @param direction - Direction of the metrics (inbound or outbound)
   * @returns Quality metrics for the specified direction
   */
  private calculateQualityMetrics(
    metrics: CollectedStats[],
    direction: 'inbound' | 'outbound'
  ): VoipCallQualitySnapshot['qualityMetrics'] | undefined {
    if (metrics.length === 0) return undefined;

    const latestMetric = metrics[metrics.length - 1];

    const baseMetrics = {
      jitter: calculateMetricAverage(metrics, (m) => m.basicStats?.jitter),
      roundTripTime: calculateMetricAverage(
        metrics,
        (m) => m.basicStats?.roundTripTime
      ),
      currentBitrate: calculateMetricAverage(
        metrics,
        (m) => m.basicStats?.availableBitrate
      ),
    };

    if (direction === 'inbound' && latestMetric.inboundAudio) {
      const packetsLost = latestMetric.basicStats?.packetsLost;

      const lastTwoMetrics = metrics.slice(-2);
      const fractionLost = calculatePacketLossRate(lastTwoMetrics);
      const bytesReceived = latestMetric.inboundAudio.bytesReceived;

      // Only return metrics if we have at least some valid data
      if (
        Object.values(baseMetrics).every((v) => isNullOrUndefined(v)) &&
        isNullOrUndefined(packetsLost) &&
        isNullOrUndefined(bytesReceived)
      ) {
        return undefined;
      }

      return {
        ...baseMetrics,
        packetsLost,
        fractionLost,
        bytesReceived,
      };
    }

    if (direction === 'outbound' && latestMetric.outboundAudio) {
      const bytesSent = latestMetric.outboundAudio.bytesSent;

      // Only return metrics if we have at least some valid data
      if (
        Object.values(baseMetrics).every((v) => isNullOrUndefined(v)) &&
        isNullOrUndefined(bytesSent)
      ) {
        return undefined;
      }

      return {
        ...baseMetrics,
        bytesSent,
      };
    }

    return undefined;
  }

  private collectSnapshots(): void {
    try {
      if (!this.peerConnection || !this.qualityAdapter) return;

      const recentSnapshot = this.metricsHistory.slice(this.lastProcessedIndex);

      if (recentSnapshot.length === 0) return;

      const latestMetric = recentSnapshot[recentSnapshot.length - 1];
      const timestamp = moment.utc().format();

      const createSnapshot = (
        direction: 'inbound' | 'outbound'
      ): VoipCallQualitySnapshot => ({
        eventName: VoipMonitorEvent.CALL_QUALITY_SNAPSHOT,
        direction,
        platform: 'web',
        timestamp,
        personId: this.personId,
        callId: this.callId,
        qualityMetrics: this.calculateQualityMetrics(recentSnapshot, direction),
        codec: this.getCodecInfo(latestMetric),
      });

      if (latestMetric.inboundAudio) {
        const inboundSnapshot = createSnapshot('inbound');
        this.emit(VoipMonitorEvent.CALL_QUALITY_SNAPSHOT, inboundSnapshot);
      }

      if (latestMetric.outboundAudio) {
        const outboundSnapshot = createSnapshot('outbound');
        this.emit(VoipMonitorEvent.CALL_QUALITY_SNAPSHOT, outboundSnapshot);
      }

      this.lastProcessedIndex = this.metricsHistory.length;
    } catch (error) {
      console.error('VoipMonitorService: Error collecting snapshots', error);
      this.emit(VoipMonitorEvent.ERROR, error as Error);
    }
  }

  private getCodecInfo(
    metrics: CollectedStats
  ): VoipCallQualitySnapshot['codec'] {
    if (!metrics?.raw) return undefined;

    const codecEntry = metrics.raw.find(isCodecReport);

    return {
      name: codecEntry?.mimeType?.split('/')[1] ?? 'unknown',
      clockRate: codecEntry?.clockRate ?? 0,
      channels: codecEntry?.channels ?? 1,
    };
  }

  /**
   * Generate the call stats
   * @returns The call stats
   */
  private generateCallStats(): CallStats {
    const metrics = this.getMetricsHistory();
    const mosScore = calculateMosScore(metrics);
    const firstMetric = metrics.length > 0 ? metrics[0] : null;
    const lastMetric = metrics.length > 0 ? metrics[metrics.length - 1] : null;

    return {
      metadata: {
        personId: this.personId,
        callId: this.callId,
        startTime: firstMetric?.time,
        endTime: moment.utc().format().toString(),
        duration: firstMetric
          ? moment.utc().diff(moment(firstMetric.time), 'seconds')
          : 0,
        codec: this.getCodecInfo(lastMetric || firstMetric),
      },
      summary: this.calculateSummaryStats(metrics, mosScore),
      ...(this.options.sendFullCallStats ? { stats: metrics } : {}),
    };
  }

  /**
   * Calculate the summary stats
   * @param metrics - The metrics
   * @param mosScore - The mos score
   * @returns The summary stats
   */
  private calculateSummaryStats(
    metrics: CollectedStats[],
    mosScore: number | undefined
  ) {
    let totalJitter = 0;
    let jitterCount = 0;
    let totalPacketsLost = 0;
    let totalRtt = 0;
    let rttCount = 0;
    let availableBitrate: number | undefined;

    metrics.forEach((metric) => {
      if (metric.basicStats) {
        if (!isNullOrUndefined(metric.basicStats.jitter)) {
          totalJitter += metric.basicStats.jitter;
          jitterCount++;
        }

        if (!isNullOrUndefined(metric.basicStats.packetsLost)) {
          totalPacketsLost += metric.basicStats.packetsLost;
        }

        if (metric.basicStats.roundTripTime) {
          totalRtt += metric.basicStats.roundTripTime;
          rttCount++;
        }

        if (!isNullOrUndefined(metric.basicStats.availableBitrate)) {
          availableBitrate = metric.basicStats.availableBitrate;
        }
      }
    });

    return {
      mosScore,
      averageJitter: jitterCount > 0 ? totalJitter / jitterCount : undefined,
      totalPacketsLost,
      averageRoundTripTime: rttCount > 0 ? totalRtt / rttCount : undefined,
      availableBitrate,
    };
  }

  /**
   * Get the metrics history
   * @returns The metrics history
   */
  public getMetricsHistory(): CollectedStats[] {
    return [...this.metricsHistory];
  }

  /**
   * Which call is currently being monitored, if any.
   *
   * Monitoring follows the current call and is skipped for conference
   * participants, whose peer connections are closed after the REFER. A reader
   * of the metrics has to know which call they belong to, or it will attribute
   * one call's quality to another.
   *
   * @returns The monitored callId, or null when monitoring is stopped
   */
  public getMonitoredCallId(): string | null {
    return this.callId || null;
  }

  /**
   * Get the latest metrics
   * @returns The latest metrics
   */
  public getLatestMetrics(): CollectedStats | null {
    return this.metricsHistory.length > 0
      ? this.metricsHistory[this.metricsHistory.length - 1]
      : null;
  }

  /**
   * Collect the stats now
   * @returns The latest metrics
   */
  public async collectStatsNow(): Promise<CollectedStats | null> {
    await this.collectStats();
    return this.getLatestMetrics();
  }

  /**
   * Clean up resources and reset state
   */
  private cleanUp(): void {
    if (this.tracksIntervalId) {
      clearInterval(this.tracksIntervalId);
      this.tracksIntervalId = null;
    }

    if (this.snapshotIntervalId) {
      clearInterval(this.snapshotIntervalId);
      this.snapshotIntervalId = null;
    }

    if (this.peerConnection) {
      this.peerConnection.removeEventListener(
        'connectionstatechange',
        this.handleConnectionStateChange
      );
      this.peerConnection.removeEventListener(
        'iceconnectionstatechange',
        this.handleIceConnectionStateChange
      );
    }

    this.peerConnection = null;
    this.metricsHistory = [];
    this.lastProcessedIndex = 0;
    this.callId = '';
    this.personId = '';
    this.qualityAdapter = undefined;
    this.lastConnectionState = 'stable';
  }

  /**
   * Handle the online event
   */
  private handleOnline = () => {
    this.isOnline = true;
    this.checkConnectionStatus();
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.checkConnectionStatus();
  };
}

export default VoipMonitorService.getInstance();
